"use client";

import { useCallback, useMemo, useSyncExternalStore } from "react";
import type { ListingFilters } from "./listing-filters";
import {
  parseListingBrowserState,
  writeListingBrowserSearch,
  type ListingBrowserState,
} from "./listing-filter-url";

export const LISTING_BROWSER_SEARCH_CHANGE_EVENT = "listing-browser-search-change";

function subscribe(onChange: () => void): () => void {
  window.addEventListener("popstate", onChange);
  window.addEventListener(LISTING_BROWSER_SEARCH_CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener("popstate", onChange);
    window.removeEventListener(LISTING_BROWSER_SEARCH_CHANGE_EVENT, onChange);
  };
}

function readSearch(): string {
  return window.location.search;
}

function readServerSearch(): string {
  return "";
}

function replaceSearch(state: ListingBrowserState) {
  const { pathname, hash, search } = window.location;
  const nextSearch = writeListingBrowserSearch(search, state);
  if (nextSearch === search) return;
  window.history.replaceState(null, "", `${pathname}${nextSearch}${hash}`);
  window.dispatchEvent(new Event(LISTING_BROWSER_SEARCH_CHANGE_EVENT));
}

export function useListingBrowserState() {
  const search = useSyncExternalStore(subscribe, readSearch, readServerSearch);
  const state = useMemo(() => parseListingBrowserState(search), [search]);

  const setFilters = useCallback(
    (filters: ListingFilters) => replaceSearch({ filters, page: 1 }),
    [],
  );
  const setPage = useCallback(
    (page: number) => replaceSearch({ filters: state.filters, page }),
    [state.filters],
  );

  return { filters: state.filters, page: state.page, setFilters, setPage };
}
